import { create } from 'zustand';

export interface NodeData {
  id: number;
  name: string;
  population: number;
  population_growth_rate: number;
  capacity_limit: number;
  resources: number;
  resource_consumption_rate: number;
  income_sources: any;
  income_status: string;
  income: number;
  workforce_ratio: number;
  emissions: number;
  traffic_volume: number;
  housing_units: number;
  stability: number;
  created_at: string;
  updated_at: string;
  x: number;
  y: number;
  z: number;
}

export interface ConnectionData {
  id: number;
  node_a_id: number;
  node_b_id: number;
  distance: number;
  traffic_volume: number;
  maintenance_cost: number;
  last_trade_volume?: number;
  last_trade_value?: number;
}

export interface ResourceNodeData {
  id:               number;
  name:             string;
  x:                number;
  y:                number;
  z:                number;
  radius:           number;
  yield_per_tick:   number;
  type:             string;
  capacity:         number;
  current_amount:   number;
  regen_rate:       number;
  max_total_shares: number;
}

export interface ResourceInvestment {
  id:           number;
  node_id:      number;
  resource_id:  number;
  shares:       number;
  created_tick: number;
}

export interface AlertData {
  id:          string;
  type:        string;
  severity:    'info' | 'warning' | 'critical';
  category:    'node' | 'connection' | 'resource' | 'policy';
  entity_id:   number | string;
  entity_name: string;
  title:       string;
  message:     string;
  value?:      number;
  tick:        number;
  created_at:  string;
}

interface SimulationState {
  nodes: NodeData[];
  connections: ConnectionData[];
  tick: number | 0;
  lastUpdate: number;
  setTick: (tick: number) => void;
  incrementTick: (tick: number) => void;
  
  isConnected: boolean;
  isReconnecting: boolean;
  simulationPaused: boolean;
  activeView: 'dashboard' | 'nodes' | 'spatial' | 'connections' | 'policies' | 'resources' | 'alerts' | 'settings';
  selectedNodeId: number | null;
  selectedConnectionId: number | null;
  
  setNodes: (nodes: NodeData[]) => void;
  setConnections: (connections: ConnectionData[]) => void;
  updateNodes: (updatedNodes: NodeData[]) => void;
  updateConnections: (updatedConnections: ConnectionData[]) => void;
  addNode: (node: NodeData) => void;
  removeNode: (id: number) => void;
  addConnection: (conn: ConnectionData) => void;
  removeConnection: (id: number) => void;
  setConnected: (connected: boolean) => void;
  setReconnecting: (reconnecting: boolean) => void;
  setSimulationPaused: (paused: boolean) => void;
  alerts:    AlertData[];
  setAlerts: (alerts: AlertData[]) => void;
  setActiveView: (view: 'dashboard' | 'nodes' | 'spatial' | 'connections' | 'policies' | 'resources' | 'alerts' | 'settings') => void;
  setSelectedNodeId: (id: number | null) => void;
  setSelectedConnectionId: (id: number | null) => void;
  
  resourceNodes:       ResourceNodeData[];
  setResourceNodes:    (rns: ResourceNodeData[]) => void;
  addResourceNode:     (rn: ResourceNodeData) => void;
  removeResourceNode:  (id: number) => void;
  // Add to SimulationState interface:
  selectedResourceNodeId: number | null;
  setSelectedResourceNodeId: (id: number | null) => void;

  investments: ResourceInvestment[];
  setInvestments: (investments: ResourceInvestment[]) => void;
}

export const useSimulationStore = create<SimulationState>((set) => ({
  nodes: [],
  connections: [],
  resourceNodes: [],
  investments: [],
  alerts: [],
  tick: 0,
  lastUpdate: Date.now(),
  isConnected: false,
  isReconnecting: false,
  simulationPaused: false,
  activeView: 'dashboard',
  selectedNodeId: null,
  selectedConnectionId: null,

  setNodes: (nodes) => {
    console.log('[STORE] setNodes called with', nodes.length, 'nodes');
    return set({ nodes: [...nodes], lastUpdate: Date.now() });
  },
  
  setAlerts:          (alerts) => set({ alerts: [...alerts], lastUpdate: Date.now() }),
  setResourceNodes:   (resourceNodes) => set({ resourceNodes: [...resourceNodes] }),
  addResourceNode:    (rn) => set(s => ({ resourceNodes: [...s.resourceNodes, rn] })),
  removeResourceNode: (id) => set(s => ({ resourceNodes: s.resourceNodes.filter(r => r.id !== id) })),
  selectedResourceNodeId: null,
  setSelectedResourceNodeId: (selectedResourceNodeId) => set({ selectedResourceNodeId }),

  setInvestments: (investments) => set({ investments: [...investments] }),
  
  setConnections: (connections) => set({ connections: [...connections], lastUpdate: Date.now() }),
  // Note: spread forces a new array reference, React/Zustand detects the change
  
  setTick: (tick) => {
    console.log('[STORE] setTick called with tick=', tick);
    return set({ tick, lastUpdate: Date.now() });
  },
  
  incrementTick: () => set(s => ({ tick: s.tick + 1 })),
  
  updateNodes: (incomingNodes) => set((state) => {
    console.log('[STORE] updateNodes called — incoming:', incomingNodes.length, 'current:', state.nodes.length);
    const updatedNodes = [...state.nodes];
    let changed = 0;
    let added = 0;
    incomingNodes.forEach(updatedNode => {
      const index = updatedNodes.findIndex(n => n.id === updatedNode.id);
      if (index !== -1) {
        const before = JSON.stringify(updatedNodes[index]);
        updatedNodes[index] = { ...updatedNodes[index], ...updatedNode };
        if (before !== JSON.stringify(updatedNodes[index])) changed++;
      } else {
        updatedNodes.push(updatedNode);
        added++;
      }
    });
    console.log('[STORE] updateNodes result — total:', updatedNodes.length, 'changed:', changed, 'added:', added);
    if (changed === 0 && added === 0) {
      console.warn('[STORE] updateNodes: NO actual data changes detected! Node data might be identical.');
    }
    return { nodes: updatedNodes, lastUpdate: Date.now() };
  }),

  updateConnections: (incomingConnections) => set((state) => {
    const updatedConnections = [...state.connections];
    incomingConnections.forEach(updatedConn => {
      const index = updatedConnections.findIndex(c => c.id === updatedConn.id);
      if (index !== -1) {
        updatedConnections[index] = { ...updatedConnections[index], ...updatedConn };
      } else {
        updatedConnections.push(updatedConn);
      }
    });
    return { connections: updatedConnections, lastUpdate: Date.now() };
  }),

  addNode: (node) => set((state) => ({ nodes: [...state.nodes, node] })),
  removeNode: (id) => set((state) => ({
    nodes: state.nodes.filter(n => n.id !== id),
    connections: state.connections.filter(c => c.node_a_id !== id && c.node_b_id !== id),
  })),
  
  addConnection: (conn) => set((state) => ({ connections: [...state.connections, conn] })),
  removeConnection: (id) => set((state) => ({ connections: state.connections.filter(c => c.id !== id) })),
  
  setConnected: (isConnected) => set({ isConnected }),
  
  setReconnecting: (isReconnecting) => set({ isReconnecting }),
  
  setSimulationPaused: (simulationPaused) => set({ simulationPaused }),
  
  setActiveView: (activeView) => set({ activeView }),
  
  setSelectedNodeId: (selectedNodeId) => set({ selectedNodeId, selectedConnectionId: null }),

  setSelectedConnectionId: (selectedConnectionId) => set({ selectedConnectionId, selectedNodeId: null }),
}));
